// src/components/UserPredictionsModal.js
import React, { useEffect, useState, useMemo, useContext } from 'react';
import predictionService from '../services/predictionService';
import leagueService from '../services/leagueService';
import MatchDaysStrip from './MatchDaysStrip';
import UserTournamentRanks from './UserTournamentRanks';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { AuthContext } from './AuthContext';
import { useTranslation } from 'react-i18next';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	IconButton,
	CircularProgress,
	Box,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Switch,
	FormControlLabel,
	useMediaQuery
} from '@mui/material';
import { trackEvent } from '../utils/analytics';
import { POINTS, POINT_STYLES } from '../utils/scoringCalculator';
import CloseIcon from '@mui/icons-material/Close';
import '../styles/UserPredictionsModal.css'

// Bodové hladiny v pořadí pro rozpad statistik.
const POINT_LEVELS = [POINTS.EXACT, POINTS.WINNER_DIFF, POINTS.WINNER, POINTS.GOALS, POINTS.MISS];

// Barvy pro režim porovnání: zobrazený hráč vs. já.
// COMPARE_THEM = tmavě modrá (jako logo Scoreo), COMPARE_ME = světle modrá aplikace.
const COMPARE_THEM = '#002855';
const COMPARE_ME = '#4DA8DA';

// Formát data hracího dne pro osu grafu: "YYYY-MM-DD" -> "d.M." (bez závislosti na časové zóně).
const formatChartDate = (isoDate) => {
	if (!isoDate || typeof isoDate !== 'string') return '';
	const [, m, d] = isoDate.split('-');
	return `${parseInt(d, 10)}.${parseInt(m, 10)}.`;
};

// Rovnoměrně rozmístěné popisky osy X (vždy včetně posledního hracího dne), ať se nepřekrývají.
const pickXAxisTicks = (points, maxTicks) => {
	const n = points.length;
	if (n <= maxTicks) return points.map(p => p.date);
	const idx = new Set();
	for (let i = 0; i < maxTicks; i++) {
		idx.add(Math.round((i * (n - 1)) / (maxTicks - 1)));
	}
	idx.add(n - 1); // poslední hrací den vždy viditelný
	return Array.from(idx).sort((a, b) => a - b).map(i => points[i].date);
};

// Celočíselné popisky pozic pro osu Y (1 = první místo nahoře).
const buildRankTicks = (maxRank, maxTicks) => {
	if (maxRank <= maxTicks) {
		return Array.from({ length: maxRank }, (_, i) => i + 1);
	}
	const ticks = new Set([1, maxRank]);
	for (let i = 1; i < maxTicks - 1; i++) {
		ticks.add(Math.round(1 + (i * (maxRank - 1)) / (maxTicks - 1)));
	}
	return Array.from(ticks).sort((a, b) => a - b);
};

// Agregace statistik tipů (jen vyhodnocené tipy s přidělenými body).
const computeStats = (predictions) => {
	const evaluatedList = (predictions || []).filter(p => p && p.points != null);
	const evaluated = evaluatedList.length;
	const buckets = POINT_LEVELS.map(value => ({
		value,
		count: evaluatedList.filter(p => p.points === value).length,
	}));
	const sumPoints = evaluatedList.reduce((acc, p) => acc + p.points, 0);
	const avgPoints = evaluated > 0 ? sumPoints / evaluated : 0;
	const hits = evaluatedList.filter(p => p.points >= POINTS.GOALS).length;
	const successRate = evaluated > 0 ? Math.round((hits / evaluated) * 100) : 0;
	return { evaluated, buckets, avgPoints, successRate };
};

const UserPredictionsModal = ({ league, userId, username, onClose, initialSelectedDate, open }) => {
	const [predictions, setPredictions] = useState([]);
	const [selectedDate, setSelectedDate] = useState(null);
	const [currentDayIndex, setCurrentDayIndex] = useState(0);
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(true);
	const [activeTab, setActiveTab] = useState(0);

	// Přihlášený uživatel – pro funkci „Porovnej se mnou".
	const { userId: currentUserId, userName: currentUserName, isLoggedIn } = useContext(AuthContext);
	const [compareMode, setCompareMode] = useState(false);
	const [myPredictions, setMyPredictions] = useState(null);
	const [myLoading, setMyLoading] = useState(false);
	const canCompare = isLoggedIn && currentUserId != null && String(currentUserId) !== String(userId);

	// Vývoj pozice uživatele v žebříčku ligy přes hrací dny (čárový graf v záložce Statistiky).
	const [rankHistory, setRankHistory] = useState(null);
	const [rankHistoryLoading, setRankHistoryLoading] = useState(false);

	// Statistiky dávají smysl jen u CASUAL lig (pevné body 10/7/4/2/0).
	const isCasual = (league?.scoringType || '').toUpperCase() === 'CASUAL';

	// Mobilní breakpoint (≤768px)
	const isMobile = useMediaQuery('(max-width:768px)');

	// Handler pro zavření modálu
	const handleClose = () => {
		onClose();
	};

	// Načtení tipů
	useEffect(() => {
		const fetchPredictions = async () => {
			setIsLoading(true);
			try {
				const data = await predictionService.getPredictionsByLeagueAndUserId(league.id, userId);
				setPredictions(data);
				trackEvent('View User Predictions', {
					league_id: league.id,
					target_user_id: userId,
					viewer_username: username
				});
			} catch (err) {
				console.error('Chyba při načítání predikcí:', err);
			} finally {
				setIsLoading(false);
			}
		};

		if (open && league && league.id && userId) {
			setActiveTab(0);
			setCompareMode(false);
			setMyPredictions(null);
			setRankHistory(null);
			fetchPredictions();
		}
	}, [league, userId, open]);

	// Lazy načtení vývoje pozice při prvním otevření záložky Statistiky (jen CASUAL ligy).
	useEffect(() => {
		if (!isCasual || activeTab !== 1 || rankHistory !== null || !league || !league.id || !userId) return;
		let cancelled = false;
		setRankHistoryLoading(true);
		leagueService.getLeagueRankingHistory(league.id, userId)
			.then((data) => { if (!cancelled) setRankHistory(data || { playerCount: 0, points: [] }); })
			.catch((err) => {
				console.error('Chyba při načítání vývoje pozice:', err);
				if (!cancelled) setRankHistory({ playerCount: 0, points: [] });
			})
			.finally(() => { if (!cancelled) setRankHistoryLoading(false); });
		return () => { cancelled = true; };
	}, [isCasual, activeTab, rankHistory, league, userId]);

	// Lazy načtení vlastních tipů při prvním zapnutí porovnání.
	useEffect(() => {
		if (!compareMode || myPredictions !== null || !league || !league.id) return;
		let cancelled = false;
		setMyLoading(true);
		predictionService.getPredictionsByLeagueAndUser(league.id)
			.then((data) => { if (!cancelled) setMyPredictions(data || []); })
			.catch((err) => {
				console.error('Chyba při načítání vlastních tipů:', err);
				if (!cancelled) setMyPredictions([]);
			})
			.finally(() => { if (!cancelled) setMyLoading(false); });
		return () => { cancelled = true; };
	}, [compareMode, myPredictions, league]);

	// Agregace statistik tipů uživatele v lize (pouze CASUAL bodování).
	const stats = useMemo(() => computeStats(predictions), [predictions]);
	const myStats = useMemo(() => computeStats(myPredictions), [myPredictions]);

	// Animace při přepnutí na záložku Statistiky: proužky vyjedou doprava a čísla
	// se přepočítají od 0 nahoru (easeOutCubic). V režimu porovnání se animují
	// hodnoty obou hráčů zároveň.
	const [barsArmed, setBarsArmed] = useState(false);
	const [displayCounts, setDisplayCounts] = useState([]);
	const [displayEvaluated, setDisplayEvaluated] = useState(0);
	const [displayAvg, setDisplayAvg] = useState(0);
	const [displaySuccess, setDisplaySuccess] = useState(0);
	const [myDisplayCounts, setMyDisplayCounts] = useState([]);
	const [myDisplayEvaluated, setMyDisplayEvaluated] = useState(0);
	const [myDisplayAvg, setMyDisplayAvg] = useState(0);
	const [myDisplaySuccess, setMyDisplaySuccess] = useState(0);

	// V porovnání čekáme i na vlastní tipy, ať čísla naskočí synchronně.
	const statsReady = compareMode ? myPredictions !== null : true;

	useEffect(() => {
		const onStats = isCasual && activeTab === 1 && stats.evaluated > 0 && statsReady;
		if (!onStats) {
			setBarsArmed(false);
			setDisplayCounts(stats.buckets.map(() => 0));
			setDisplayEvaluated(0);
			setDisplayAvg(0);
			setDisplaySuccess(0);
			setMyDisplayCounts(myStats.buckets.map(() => 0));
			setMyDisplayEvaluated(0);
			setMyDisplayAvg(0);
			setMyDisplaySuccess(0);
			return undefined;
		}

		// Proužky odstartují na nule (barsArmed=false) a na další snímek vyjedou na cíl.
		setBarsArmed(false);
		const armId = requestAnimationFrame(() => setBarsArmed(true));

		const duration = 800;
		const start = performance.now();
		let rafId;
		const tick = (now) => {
			const p = Math.min(1, (now - start) / duration);
			const eased = 1 - Math.pow(1 - p, 3);
			setDisplayCounts(stats.buckets.map(b => Math.round(b.count * eased)));
			setDisplayEvaluated(Math.round(stats.evaluated * eased));
			setDisplayAvg(stats.avgPoints * eased);
			setDisplaySuccess(Math.round(stats.successRate * eased));
			if (compareMode) {
				setMyDisplayCounts(myStats.buckets.map(b => Math.round(b.count * eased)));
				setMyDisplayEvaluated(Math.round(myStats.evaluated * eased));
				setMyDisplayAvg(myStats.avgPoints * eased);
				setMyDisplaySuccess(Math.round(myStats.successRate * eased));
			}
			if (p < 1) rafId = requestAnimationFrame(tick);
		};
		rafId = requestAnimationFrame(tick);

		return () => {
			cancelAnimationFrame(armId);
			cancelAnimationFrame(rafId);
		};
	}, [isCasual, activeTab, stats, compareMode, myStats, statsReady]);

	const uniqueMatchDates = useMemo(() => {
		if (!league || !league.tournament || !league.tournament.matches) return [];

		const now = new Date();
		now.setHours(23, 59, 59, 999);
		const leagueCreationDate = new Date(league.createdAt);
		const pastDates = league.tournament.matches
			.filter(match => new Date(match.date) >= leagueCreationDate)
			.map(m => new Date(new Date(m.date).setHours(0, 0, 0, 0)))
			.filter(d => d <= now);
		const unique = Array.from(new Set(pastDates.map(d => d.getTime())))
			.map(t => new Date(t))
			.sort((a, b) => a - b);
		return unique;
	}, [league]);

	useEffect(() => {
		if (uniqueMatchDates.length === 0 || !initialSelectedDate) return;

		const matchingDate = uniqueMatchDates.find(d => d.toDateString() === initialSelectedDate.toDateString());
		if (matchingDate) {
			setSelectedDate(matchingDate);
			const index = uniqueMatchDates.findIndex(d => d.getTime() === matchingDate.getTime());
			setCurrentDayIndex(Math.max(0, index - 1));
		} else {
			const lastDate = uniqueMatchDates[uniqueMatchDates.length - 1];
			setSelectedDate(lastDate);
			const lastIndex = uniqueMatchDates.findIndex(d => d.getTime() === lastDate.getTime());
			setCurrentDayIndex(Math.max(0, lastIndex - 2));
		}
	}, [uniqueMatchDates, initialSelectedDate]);

	const filteredMatches = useMemo(() => {
		if (!selectedDate || !league || !league.tournament || !league.tournament.matches) return [];

		const leagueCreationDate = new Date(league.createdAt);
		return league.tournament.matches
			.filter(match => new Date(match.date) >= leagueCreationDate)
			.filter(m => new Date(m.date).toDateString() === selectedDate.toDateString())
			.filter(m => !['NS', 'Not Started', 'TBD'].includes(m.matchStatus))
			.sort((a, b) => {
				const dateA = new Date(a.date);
				const dateB = new Date(b.date);
				if (dateA.getTime() !== dateB.getTime()) {
					return dateA - dateB;
				}
				return a.id - b.id;
			});
	}, [league, selectedDate]);

	const getPredictionForMatch = (matchId) =>
		predictions && predictions.length > 0
			? predictions.find(p => p.matchId === matchId)
			: null;

	if (!league || !league.tournament || !league.tournament.matches) {
		return (
			<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
				<DialogTitle>
					{t('common.error')}
					<IconButton
						aria-label="close"
						onClick={handleClose}
						sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent dividers>
					<Typography>{t('leagueDetails.errorLoadingData')}</Typography>
				</DialogContent>
			</Dialog>
		);
	}

	const headCellBaseSx = {
		backgroundColor: '#F9F9F9',
		color: '#787878',
		fontWeight: 300,
		textTransform: 'uppercase',
		fontSize: isMobile ? '10px' : '14px',
		letterSpacing: '0.5px',
		border: 'none',
		fontFamily: 'Montserrat, sans-serif',
		p: isMobile ? '6px 4px' : '8px 16px',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis'
	};

	const bodyCellBaseSx = {
		border: 'none',
		fontSize: isMobile ? '10px' : '14px',
		p: isMobile ? '6px 4px' : '8px 16px',
		overflow: 'hidden'
	};

	// Nadpis „Rozpad podle bodů" s přepínačem porovnání vpravo (sdílený pro oba režimy).
	const breakdownHeader = (
		<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, minHeight: 32 }}>
			<Typography sx={{ color: '#787878', fontSize: isMobile ? '11px' : '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
				{t('userPredictions.statsBreakdown')}
			</Typography>
			{canCompare && (
				<FormControlLabel
					control={
						<Switch
							size="small"
							checked={compareMode}
							onChange={(e) => {
								const next = e.target.checked;
								setCompareMode(next);
								if (next) {
									trackEvent('Compare User Stats', {
										league_id: league.id,
										target_user_id: userId,
										viewer_username: currentUserName
									});
								}
							}}
							sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: COMPARE_THEM }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: COMPARE_THEM } }}
						/>
					}
					label={t('userPredictions.statsCompare')}
					labelPlacement="start"
					sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: isMobile ? '11px' : '13px', color: COMPARE_THEM, fontWeight: 600 } }}
				/>
			)}
		</Box>
	);

	// JSX renderování modálu
	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ m: 0, p: isMobile ? 1.5 : 2, position: 'relative' }}>
				<IconButton
					aria-label="close"
					onClick={handleClose}
					sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
				>
					<CloseIcon />
				</IconButton>
				{isCasual && (
					<div className="up-tabs" role="tablist" data-active={activeTab === 1 ? 'stats' : 'predictions'}>
						<span className="up-tab-slider" aria-hidden="true" />
						<button
							type="button"
							role="tab"
							aria-selected={activeTab === 0}
							className={`up-tab-pill ${activeTab === 0 ? 'is-active' : ''}`}
							onClick={() => setActiveTab(0)}
						>
							{t('userPredictions.tabPredictions')}
						</button>
						<button
							type="button"
							role="tab"
							aria-selected={activeTab === 1}
							className={`up-tab-pill ${activeTab === 1 ? 'is-active' : ''}`}
							onClick={() => {
								setActiveTab(1);
								trackEvent('View User Stats', {
									league_id: league.id,
									target_user_id: userId,
									viewer_username: username
								});
							}}
						>
							{t('userPredictions.tabStats')}
						</button>
					</div>
				)}
				<Typography
					variant="h6"
					sx={{ fontSize: isMobile ? '16px' : '20px', fontWeight: 700, pr: 5, textAlign: 'center' }}
					noWrap
				>
					{t('tipsOfUser')} {username}
				</Typography>
			</DialogTitle>
			<DialogContent dividers>
				{isLoading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
						<CircularProgress />
					</Box>
				) : (
					<>
						{(!isCasual || activeTab === 0) && (
						<>
						{uniqueMatchDates && uniqueMatchDates.length > 0 ? (
							<MatchDaysStrip
								allDates={uniqueMatchDates}
								currentDayIndex={currentDayIndex}
								setCurrentDayIndex={setCurrentDayIndex}
								selectedDate={selectedDate}
								setSelectedDate={setSelectedDate}
								league={league}
							/>
						) : (
							<Typography sx={{ textAlign: 'center', p: 2 }}>{t('leagueDetails.nodates')}</Typography>
						)}
						<TableContainer component={Paper} sx={{ mt: isMobile ? 2 : 3, boxShadow: 'none' }}>
							<Table 
								className="user-modal-table" 
								size={isMobile ? 'small' : 'medium'}
								sx={{ tableLayout: 'fixed', width: '100%' }}
							>
								{/* Upravené šířky sloupců */}
								<colgroup>
									<col style={{ width: '30%' }} /> {/* Zmenšeno z 40% na 30% */}
									<col style={{ width: '30%' }} /> {/* Zvětšeno z 20% na 30% */}
									<col style={{ width: '20%' }} />
									<col style={{ width: '20%' }} />
								</colgroup>
								<TableHead>
									<TableRow>
										<TableCell sx={{ ...headCellBaseSx, borderTopLeftRadius: isMobile ? '4px' : '8px' }} align="left">
											{t('match')}
										</TableCell>
										<TableCell sx={headCellBaseSx} align="center">
											{t('result')}
										</TableCell>
										<TableCell sx={headCellBaseSx} align="center">
											Tip
										</TableCell>
										<TableCell sx={{ ...headCellBaseSx, borderTopRightRadius: isMobile ? '4px' : '8px' }} align="center">
											{t('leagueDetails.points')}
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{filteredMatches.length > 0 ? (
										filteredMatches.map((match) => {
											const prediction = getPredictionForMatch(match.id);
											const realScore = `${match.scoreHome ?? '-'} : ${match.scoreAway ?? '-'}`;
											const points = prediction?.points ?? '–';

											// U tipnuté remízy s vybraným postupujícím se "X" zobrazí
											// přímo u skóre strany, která podle tipu postupuje.
											const showAdvancing = prediction?.drawInRegularTime === true;
											const homeAdvancing = showAdvancing && (
												prediction.advancingTeamId
													? prediction.advancingTeamId === match.homeTeam.id
													: prediction.predictedHomeScore > prediction.predictedAwayScore
											);
											const awayAdvancing = showAdvancing && (
												prediction.advancingTeamId
													? prediction.advancingTeamId === match.awayTeam.id
													: prediction.predictedAwayScore > prediction.predictedHomeScore
											);

											return (
												<TableRow
													key={match.id}
													sx={{
														'&:nth-of-type(odd)': { backgroundColor: 'transparent !important' },
														'&:nth-of-type(even)': { backgroundColor: '#FAFAFA !important' },
														'&:last-child td, &:last-child th': { border: 0 },
														borderBottom: '1px solid #e0e0e0',
														'&:hover': {
															backgroundColor: '#FAFAFA !important',
															'& td': { backgroundColor: '#FAFAFA !important' }
														},
													}}
												>
													<TableCell sx={bodyCellBaseSx} align="left">
														<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', maxWidth: '100%' }}>
															<Typography 
																variant="body2" 
																noWrap 
																sx={{ 
																	fontSize: isMobile ? '11px' : '14px', 
																	width: '100%', 
																	display: 'block' 
																}}
																title={match.homeTeam.name}
															>
																{t(`teams.${match.homeTeam.name}`, { defaultValue: match.homeTeam.name })}
															</Typography>
															<Typography 
																variant="body2" 
																noWrap 
																sx={{ 
																	fontSize: isMobile ? '11px' : '14px', 
																	width: '100%', 
																	display: 'block' 
																}}
																title={match.awayTeam.name}
															>
																{t(`teams.${match.awayTeam.name}`, { defaultValue: match.awayTeam.name })}
															</Typography>
														</Box>
													</TableCell>
													<TableCell sx={bodyCellBaseSx} align="center">
														<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: '100%' }}>
															<Typography variant="body2" noWrap sx={{ fontSize: isMobile ? '11px' : '14px' }}>
																{realScore}
															</Typography>
															<Typography variant="caption" noWrap sx={{ fontSize: isMobile ? '9px' : '12px', width: '100%', display: 'block' }}>
																{t(`matchStatus.${match.matchStatus}`, { defaultValue: match.matchStatus })}
															</Typography>
														</Box>
													</TableCell>
													<TableCell sx={bodyCellBaseSx} align="center">
														<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: '100%' }}>
															<Typography variant="body2" noWrap sx={{ fontSize: isMobile ? '11px' : '14px' }}>
																{prediction ? (
																	<>
																		{prediction.predictedHomeScore}
																		{homeAdvancing && (
																			<Box component="sup" sx={{ ml: '1px', fontSize: '0.7em' }}>X</Box>
																		)}
																		{' : '}
																		{prediction.predictedAwayScore}
																		{awayAdvancing && (
																			<Box component="sup" sx={{ ml: '1px', fontSize: '0.7em' }}>X</Box>
																		)}
																	</>
																) : '–'}
															</Typography>
														</Box>
													</TableCell>
													<TableCell sx={bodyCellBaseSx} align="center">
														<Typography variant="body2" noWrap sx={{ fontSize: isMobile ? '11px' : '14px' }}>
															{points}
														</Typography>
													</TableCell>
												</TableRow>
											);
										})
									) : (
										<TableRow>
											<TableCell colSpan={4} sx={{ textAlign: 'center', ...bodyCellBaseSx }}>
												{t('leagueDetails.nomatches')}
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
						</>
						)}
						{isCasual && activeTab === 1 && (
							<Box sx={{ py: isMobile ? 1 : 2 }}>
								{stats.evaluated === 0 ? (
									<Typography sx={{ textAlign: 'center', p: 2 }}>{t('userPredictions.statsNoData')}</Typography>
								) : (
									<>
										{compareMode ? (
											myLoading ? (
												<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
											) : myStats.evaluated === 0 ? (
												<Typography sx={{ textAlign: 'center', p: 2 }}>{t('userPredictions.statsCompareEmpty')}</Typography>
											) : (
												<>
													<Box sx={{ display: 'flex', gap: isMobile ? 1 : 2, mb: isMobile ? 2 : 3 }}>
														{[
															{ label: t('userPredictions.statsEvaluatedTips'), them: displayEvaluated, mine: myDisplayEvaluated },
															{ label: t('userPredictions.statsAvgPoints'), them: displayAvg.toFixed(1), mine: myDisplayAvg.toFixed(1) },
															{ label: t('userPredictions.statsSuccessRate'), them: `${displaySuccess}%`, mine: `${myDisplaySuccess}%` },
														].map((tile, i) => (
															<Box key={i} sx={{ flex: 1, textAlign: 'center', backgroundColor: '#F9F9F9', borderRadius: '8px', p: isMobile ? 1 : 1.5 }}>
																<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: 0.5 }}>
																	<Typography sx={{ fontWeight: 700, fontSize: isMobile ? '14px' : '20px', fontFamily: 'Montserrat, sans-serif', color: COMPARE_THEM }}>{tile.them}</Typography>
																	<Typography sx={{ color: '#ccc', fontSize: isMobile ? '11px' : '13px' }}>/</Typography>
																	<Typography sx={{ fontWeight: 700, fontSize: isMobile ? '14px' : '20px', fontFamily: 'Montserrat, sans-serif', color: COMPARE_ME }}>{tile.mine}</Typography>
																</Box>
																<Typography sx={{ color: '#787878', fontSize: isMobile ? '9px' : '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{tile.label}</Typography>
															</Box>
														))}
													</Box>
													{breakdownHeader}
													<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: isMobile ? 0.75 : 1 }}>
														<Typography noWrap sx={{ fontWeight: 700, fontSize: isMobile ? '11px' : '13px', color: COMPARE_THEM, maxWidth: '48%' }}>{username}</Typography>
														<Typography noWrap sx={{ fontWeight: 700, fontSize: isMobile ? '11px' : '13px', color: COMPARE_ME, maxWidth: '48%' }}>{t('userPredictions.statsYou')}</Typography>
													</Box>
													{stats.buckets.map(({ value, count }, i) => {
														const style = POINT_STYLES[value];
														const myCount = myStats.buckets[i] ? myStats.buckets[i].count : 0;
														const total = count + myCount;
														const themPct = total > 0 ? (count / total) * 100 : 0;
														const myPct = total > 0 ? (myCount / total) * 100 : 0;
														return (
															<Box key={value} sx={{ mb: isMobile ? 1 : 1.25 }}>
																<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
																	<Typography sx={{ width: isMobile ? 26 : 34, textAlign: 'left', fontWeight: 700, fontSize: isMobile ? '12px' : '14px', color: COMPARE_THEM }}>{displayCounts[i] ?? 0}</Typography>
																	<Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
																		<Box component="span" sx={{ fontSize: isMobile ? '13px' : '15px' }}>{style.emoji}</Box>
																		<Typography noWrap sx={{ fontSize: isMobile ? '10px' : '12px', color: '#333' }}>{t(style.label)}</Typography>
																	</Box>
																	<Typography sx={{ width: isMobile ? 26 : 34, textAlign: 'right', fontWeight: 700, fontSize: isMobile ? '12px' : '14px', color: COMPARE_ME }}>{myDisplayCounts[i] ?? 0}</Typography>
																</Box>
																<Box className="up-compare-bar">
																	<Box className="up-compare-seg" sx={{ width: barsArmed ? `${themPct}%` : '0%', backgroundColor: COMPARE_THEM }} />
																	<Box className="up-compare-seg" sx={{ width: barsArmed ? `${myPct}%` : '0%', backgroundColor: COMPARE_ME }} />
																</Box>
															</Box>
														);
													})}
												</>
											)
										) : (
										<>
										<Box sx={{ display: 'flex', gap: isMobile ? 1 : 2, mb: isMobile ? 2 : 3 }}>
											{[
												{ label: t('userPredictions.statsEvaluatedTips'), value: displayEvaluated },
												{ label: t('userPredictions.statsAvgPoints'), value: displayAvg.toFixed(1) },
												{ label: t('userPredictions.statsSuccessRate'), value: `${displaySuccess}%` },
											].map((tile, i) => (
												<Box key={i} sx={{ flex: 1, textAlign: 'center', backgroundColor: '#F9F9F9', borderRadius: '8px', p: isMobile ? 1 : 1.5 }}>
													<Typography sx={{ fontWeight: 700, fontSize: isMobile ? '18px' : '24px', fontFamily: 'Montserrat, sans-serif' }}>
														{tile.value}
													</Typography>
													<Typography sx={{ color: '#787878', fontSize: isMobile ? '9px' : '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
														{tile.label}
													</Typography>
												</Box>
											))}
										</Box>
										{breakdownHeader}
										{stats.buckets.map(({ value, count }, i) => {
											const style = POINT_STYLES[value];
											const pct = stats.evaluated > 0 ? (count / stats.evaluated) * 100 : 0;
											return (
												<Box key={value} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: isMobile ? 0.75 : 1 }}>
													<Box sx={{ width: isMobile ? 120 : 160, display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
														<Box component="span" sx={{ fontSize: isMobile ? '14px' : '16px' }}>{style.emoji}</Box>
														<Typography noWrap sx={{ fontSize: isMobile ? '11px' : '13px', color: '#333' }}>
															{t(style.label)}
														</Typography>
													</Box>
													<Box sx={{ flex: 1, height: isMobile ? 8 : 10, backgroundColor: '#F0F0F0', borderRadius: '6px', overflow: 'hidden' }}>
														<Box
															className="up-stat-bar-fill"
															sx={{ width: barsArmed ? `${pct}%` : '0%', backgroundColor: style.color }}
														/>
													</Box>
													<Typography sx={{ width: isMobile ? 28 : 36, textAlign: 'right', fontWeight: 700, fontSize: isMobile ? '12px' : '14px', color: style.color }}>
														{displayCounts[i] ?? 0}
													</Typography>
												</Box>
											);
										})}
										</>
										)}
									</>
								)}

								{/* Vývoj pozice v žebříčku ligy přes hrací dny (čárový graf). */}
								<Box sx={{ mt: isMobile ? 2 : 3 }}>
									<Typography sx={{ color: '#787878', fontSize: isMobile ? '11px' : '14px', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
										{t('userPredictions.statsPositionTitle')}
									</Typography>
									{rankHistoryLoading ? (
										<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
									) : !rankHistory || !rankHistory.points || rankHistory.points.length === 0 ? (
										<Typography sx={{ textAlign: 'center', p: 2, color: '#787878', fontSize: isMobile ? '12px' : '14px' }}>
											{t('userPredictions.statsPositionNoData')}
										</Typography>
									) : (() => {
										const chartPoints = rankHistory.points;
										// Spodní mez osy Y = nejhorší dosažená pozice (min. 2, aby osa nezkolabovala u stálé 1. příčky).
										const maxRank = Math.max(2, ...chartPoints.map(p => p.rank));
										const xTicks = pickXAxisTicks(chartPoints, isMobile ? 4 : 6);
										const yTicks = buildRankTicks(maxRank, isMobile ? 5 : 7);
										return (
										<ResponsiveContainer width="100%" height={isMobile ? 200 : 260}>
											<LineChart data={chartPoints} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
												<CartesianGrid strokeDasharray="3 3" stroke="#EEEEEE" vertical={false} />
												<XAxis
													dataKey="date"
													ticks={xTicks}
													interval={0}
													tickFormatter={formatChartDate}
													tick={{ fontSize: isMobile ? 10 : 12, fill: '#787878' }}
													tickLine={false}
													axisLine={{ stroke: '#E0E0E0' }}
												/>
												<YAxis
													reversed
													allowDecimals={false}
													domain={[1, maxRank]}
													ticks={yTicks}
													interval={0}
													tickFormatter={(v) => Math.round(v)}
													tick={{ fontSize: isMobile ? 10 : 12, fill: '#787878' }}
													tickLine={false}
													axisLine={{ stroke: '#E0E0E0' }}
													width={isMobile ? 28 : 36}
												/>
												<Tooltip
													content={({ active, payload }) => {
														if (!active || !payload || !payload.length) return null;
														const p = payload[0].payload;
														return (
															<Box sx={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '6px', p: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
																<Typography sx={{ fontSize: '11px', color: '#787878' }}>{formatChartDate(p.date)}</Typography>
																<Typography sx={{ fontSize: '13px', fontWeight: 700, color: COMPARE_THEM }}>
																	{t('userPredictions.statsPositionTooltip', { rank: p.rank })}
																</Typography>
															</Box>
														);
													}}
												/>
												<Line
													type="monotone"
													dataKey="rank"
													stroke={COMPARE_THEM}
													strokeWidth={2}
													dot={{ r: 3, fill: COMPARE_THEM }}
													activeDot={{ r: 5 }}
													isAnimationActive
												/>
											</LineChart>
										</ResponsiveContainer>
										);
									})()}
								</Box>

								{/* Účast v turnajích a umístění v globálním žebříčku. */}
								<Box sx={{ mt: isMobile ? 2 : 3 }}>
									<Typography sx={{ color: '#787878', fontSize: isMobile ? '11px' : '14px', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
										{t('tournamentRanks.modalTitle')}
									</Typography>
									<UserTournamentRanks userId={userId} />
								</Box>
							</Box>
						)}
						</>
					)}
				</DialogContent>
		</Dialog>
	);
};

export default UserPredictionsModal;